import axios from 'axios';
import toast from 'react-hot-toast';

import { api as config } from '../../global/config/api';
import { ApiError } from './error';
import { broadcastLogout, readCsrfCookie } from './session';

// TSS vite/04 — the canonical HTTP client. Layer 1.
// All app code goes through this instance (directly or via useQuery / useBridge).

const MUTATING = new Set(['post', 'put', 'patch', 'delete']);

export const api = axios.create({
    baseURL: config.endpoint,
    timeout: 15_000,
    withCredentials: true,
});

api.interceptors.request.use((cfg) => {
    inferContentType(cfg);

    // Respect a caller-provided X-CSRF-Token: the invite flow (POST public/register/:token)
    // echoes its own pre-session cookie (csrf_token_register, 0046 §8) — the session
    // csrf_token must not overwrite it when both cookies coexist.
    if (MUTATING.has(String(cfg.method).toLowerCase()) && !cfg.headers['X-CSRF-Token']) {
        const csrf = readCsrfCookie();
        if (csrf) cfg.headers['X-CSRF-Token'] = csrf;
    }

    cfg.metadata = { id: crypto.randomUUID(), start: performance.now() };
    cfg.headers['X-Request-ID'] = cfg.metadata.id;

    return cfg;
});

api.interceptors.response.use(
    (res) => {
        if (import.meta.env.DEV) {
            const ms = (performance.now() - res.config.metadata.start).toFixed(0);
            console.debug(`[api] ${res.config.method?.toUpperCase()} ${res.config.url} → ${res.status} (${ms}ms)`);
        }
        return res;
    },
    async (error) => {
        const apiError = ApiError.fromAxios(error);

        if (apiError.status === 401) handle401(error);
        if (apiError.status === 403) handle403();

        throw apiError;
    },
);

// TSS vite/04 §"Content-type auto-detection". Sniffs cfg.data and sets
// (or removes) the Content-Type header. Caller never sets it manually.
function inferContentType(cfg) {
    if (cfg.data == null) return;
    if (cfg.headers['Content-Type']) return;

    if (cfg.data instanceof FormData) {
        // Browser must set the multipart boundary; pre-setting breaks it.
        delete cfg.headers['Content-Type'];
        return;
    }
    if (cfg.data instanceof Blob) {
        cfg.headers['Content-Type'] = cfg.data.type || 'application/octet-stream';
        return;
    }
    if (cfg.data instanceof URLSearchParams) {
        cfg.headers['Content-Type'] = 'application/x-www-form-urlencoded';
        return;
    }
    if (cfg.data instanceof ArrayBuffer || ArrayBuffer.isView(cfg.data)) {
        cfg.headers['Content-Type'] = 'application/octet-stream';
        return;
    }
    if (typeof cfg.data === 'object') {
        cfg.headers['Content-Type'] = 'application/json';
    }
}

// Per TSS vite/05 §"Global error side effects" — only 401 and 403
// trigger global behavior. Everything else is the caller's job.

function handle401(error) {
    const url = error?.config?.url || '';
    // Session-establishing endpoints bypass the global redirect — a 401 there
    // is "not logged in yet / bad credentials", handled by the login form and
    // the AuthProvider bootstrap. Only an expired session on a data call
    // (e.g. public/recipients) should kick the user out.
    if (isSessionEndpoint(url)) return;
    broadcastLogout();
    // AuthProvider listens for 'logout_signal' storage events and clears state.
    window.location.href = '/login';
}

// public/auth/login, public/auth/refresh, public/auth/logout, public/me,
// public/permissions — the auth surface of the public channel (TSS vite/05).
function isSessionEndpoint(url) {
    return url.includes('/auth/')
        || /(^|\/)me$/.test(url)
        || /(^|\/)permissions$/.test(url);
}

let forbiddenToastActive = false;
function handle403() {
    if (forbiddenToastActive) return;
    forbiddenToastActive = true;
    toast.error('No tienes permiso para esta acción');
    setTimeout(() => { forbiddenToastActive = false; }, 2000);
    window.dispatchEvent(new CustomEvent('auth:permissions-refresh'));
}

// Convenience helpers. TSS vite/04 §"File uploads" / §"File downloads".

export async function uploadFile(endpoint, file, fields = {}, opts = {}) {
    const { onProgress, signal, fileField = 'file' } = opts;
    const form = new FormData();
    form.append(fileField, file);
    for (const [k, v] of Object.entries(fields)) form.append(k, String(v));
    const res = await api.post(endpoint, form, {
        onUploadProgress: onProgress,
        signal,
    });
    return res.data;
}

export async function downloadFile(endpoint, opts = {}) {
    const res = await api.get(endpoint, {
        responseType: 'blob',
        signal: opts.signal,
    });
    return res.data;
}
